import React, { useEffect, useState } from 'react';
import {
    Anchor,
    Box,
    Button,
    DataTable,
    Heading,
    Meter,
    Notification,
    Page,
    PageContent,
    Pagination,
    Text,
} from 'grommet';
import { Trash } from 'grommet-icons';
import { useLoaderData, useNavigate, useRevalidator } from 'react-router-dom';
import {
    API,
    CompanyService,
    RecentActivity,
    Utils,
    jobStatColumns,
} from '@enrich/shared-lib';
import QueryString from 'qs';
import { AddCompanyPipelineButton } from './AddCompanyPipelineButton';
import { AddCompanyReportRecipientButton } from './AddCompanyReportRecipientButton';
import { ViewClosedJobsButton } from './ViewClosedJobsButton';
import { CompanyManualReportButton } from './CompanyManualReportButton';
import { EditCompanyButton } from './EditCompanyButton';

const CANDIDACY_STATUSES = [
    { label: 'interested', color: 'status-ok' },
    { label: 'missed_apply_deadline', color: 'status-warning' },
    { label: 'applied', color: 'brand' },
    { label: 'application_rejected', color: 'status-critical' },
    { label: 'assessment', color: 'accent-1' },
    { label: 'interview', color: 'accent-2' },
    { label: 'second_interview', color: 'accent-3' },
    { label: 'offer_received', color: 'accent-4' },
    { label: 'offer_accepted', color: 'graph-1' },
    { label: 'offer_rejected', color: 'graph-2' },
];

const PIPELINE_CANDIDACY_STATUSES = [
    ...CANDIDACY_STATUSES.map((s) => s.label),
    'closed',
];

export const CompanyProfile = () => {
    const company = useLoaderData();
    const jobs = company.attributes.jobs?.data || [];
    const pipelines = company.attributes.pipelines?.data || [];
    const revalidator = useRevalidator();
    const [notification, setNotification] = useState(null);

    const reportRecipientTableStepOptions = [5, 10];
    const reportRecipientsData =
        company.attributes.reportRecipients?.map((r, i) => ({
            id: i + 1,
            email: r,
        })) || [];

    const [reportRecipients, setReportRecipients] =
        useState(reportRecipientsData);
    const [reportRecipientPage, setReportRecipientPage] = useState(1);
    const [totalReportRecipientItems, setTotalReportRecipientItems] =
        useState(0);
    const [reportRecipientPageSize, setReportRecipientPageSize] = useState(
        reportRecipientTableStepOptions[0],
    );

    useEffect(() => {
        if (!company.attributes.isReportClient || !reportRecipientPage) return;

        const pagedReportRecipients = reportRecipientsData.slice(
            reportRecipientPage * reportRecipientPageSize -
                reportRecipientPageSize,
            reportRecipientPage * reportRecipientPageSize,
        );

        setReportRecipientPage(reportRecipientPage);
        setReportRecipients(pagedReportRecipients);
        setTotalReportRecipientItems(reportRecipientsData.length);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        reportRecipientPage,
        reportRecipientPageSize,
        company.attributes.reportRecipients,
    ]);

    const handleReportRecipientPageChange = ({
        page,
        startIndex,
        endIndex,
    }) => {
        const newPageSize = endIndex - startIndex;

        if (reportRecipientPageSize != newPageSize) {
            setReportRecipientPageSize(newPageSize);
            setReportRecipientPage(1);
            return;
        }
        setReportRecipientPage(page);
    };

    const reportRecipientDataColumns = [
        {
            header: 'Email',
            size: 'large',
            property: 'email',
            sortable: true,
        },
        {
            header: 'Action',
            size: 'small',
            property: 'id',
            primary: true,
            // eslint-disable-next-line react/display-name
            render: (recipient) => {
                const [buttonBusy, setButtonBusy] = useState(false);

                return (
                    <Button
                        pad="none"
                        color="enrich-red"
                        size="small"
                        plain
                        busy={buttonBusy}
                        label={
                            <Trash
                                size="medium"
                                color="red"
                                onClick={async () => {
                                    setButtonBusy(true);
                                    try {
                                        const updated = reportRecipientsData
                                            .filter(
                                                (x) =>
                                                    x.email !== recipient.email,
                                            )
                                            .map((x) => x.email);

                                        await new CompanyService().update(
                                            company.id,
                                            { reportRecipients: updated },
                                        );

                                        setNotification({
                                            status: 'normal',
                                            title: 'Success',
                                            message:
                                                'Report recipient removed.',
                                        });
                                        revalidator.revalidate();
                                    } catch (error) {
                                        console.log(error);
                                        const message =
                                            error.response?.data?.error
                                                ?.message ||
                                            error.message ||
                                            'Failed to remove report recipient.';
                                        setNotification({
                                            status: 'critical',
                                            title: 'Remove failed',
                                            message,
                                        });
                                    }
                                    setButtonBusy(false);
                                }}
                            />
                        }
                    />
                );
            },
            sortable: false,
        },
    ];

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent gap="medium">
                <Box direction="row" justify="between" align="center">
                    <Heading level="2" margin={{ vertical: 'none' }}>
                        {company.attributes.name}
                    </Heading>
                    <Box direction="row" gap="medium" align="center">
                        <EditCompanyButton company={company} />
                        <CompanyManualReportButton company={company} />
                    </Box>
                </Box>
                {company.attributes.isContractClient && (
                    <Box
                        alignSelf="start"
                        background="enrich-green-light"
                        round="6px"
                        pad="small">
                        <Text>Contract Client</Text>
                    </Box>
                )}

                <Box>
                    <Box
                        direction="row-responsive"
                        fill="horizontal"
                        justify="between">
                        <Box>
                            <Heading level="3">Jobs</Heading>
                        </Box>
                        <Box alignSelf="center">
                            <ViewClosedJobsButton
                                closedJobs={jobs.filter(
                                    (j) =>
                                        j.attributes.status.toLowerCase() ==
                                        'closed',
                                )}
                            />
                        </Box>
                    </Box>
                    <Box elevation="medium" pad="medium">
                        {jobs?.length > 0 ? (
                            <Box overflow={{ vertical: 'auto' }}>
                                <DataTable
                                    sortable
                                    data={jobs
                                        .filter(
                                            (j) =>
                                                j.attributes.status.toLowerCase() !==
                                                'closed',
                                        )
                                        .map((j) => ({
                                            id: j.id,
                                            ...j.attributes,
                                        }))}
                                    columns={[
                                        {
                                            header: 'Job Title',
                                            property: 'title',
                                            render: (job) => (
                                                <Anchor
                                                    href={`/jobs/${job.id}`}
                                                    label={job.title}
                                                />
                                            ),
                                            sortable: true,
                                        },
                                        ...jobStatColumns({ sortable: true }),
                                    ]}
                                />
                            </Box>
                        ) : (
                            'No Job available'
                        )}
                    </Box>
                </Box>

                {company.attributes.slug && (
                    <CompanyDashboardSection
                        companyId={company.id}
                        companySlug={company.attributes.slug}
                        admins={
                            company.attributes.companyAdmins?.data?.map(
                                (a) => a.attributes,
                            ) || []
                        }
                        reportRecipientsSection={
                            company.attributes.isReportClient ? (
                                <Box
                                    direction="row-responsive"
                                    fill="horizontal">
                                    <Box basis="xlarge" fill="horizontal">
                                        <Box
                                            direction="row-responsive"
                                            fill="horizontal"
                                            pad={{ right: 'small' }}
                                            gap="medium">
                                            <Box
                                                basis="large"
                                                fill="horizontal">
                                                <Heading level="3">
                                                    Report Recipients
                                                </Heading>
                                            </Box>
                                            <Box
                                                basis="small"
                                                justify="center"
                                                fill="horizontal">
                                                <AddCompanyReportRecipientButton
                                                    companyId={company.id}
                                                    recipients={reportRecipientsData.map(
                                                        (x) => x.email,
                                                    )}
                                                    setNotification={
                                                        setNotification
                                                    }
                                                />
                                            </Box>
                                        </Box>

                                        <Box
                                            direction="row-responsive"
                                            elevation="large"
                                            pad="medium"
                                            fill="horizontal">
                                            <DataTable
                                                sortable
                                                data={reportRecipients}
                                                columns={
                                                    reportRecipientDataColumns
                                                }
                                                fill="horizontal"
                                            />
                                        </Box>

                                        <Box
                                            align="center"
                                            justify="center"
                                            margin={{
                                                top: 'medium',
                                                bottom: 'none',
                                            }}>
                                            <Pagination
                                                numberItems={
                                                    totalReportRecipientItems
                                                }
                                                onChange={({
                                                    page,
                                                    startIndex,
                                                    endIndex,
                                                }) => {
                                                    handleReportRecipientPageChange(
                                                        {
                                                            page,
                                                            startIndex,
                                                            endIndex,
                                                        },
                                                    );
                                                }}
                                                size="small"
                                                step={reportRecipientPageSize}
                                                stepOptions={
                                                    reportRecipientTableStepOptions
                                                }
                                                summary={true}
                                            />
                                        </Box>
                                    </Box>
                                    <Box basis="large" />
                                </Box>
                            ) : null
                        }
                    />
                )}

                <Box>
                    <Box direction="row-responsive" gap="medium">
                        <Box>
                            <Heading level="3">Pipelines</Heading>
                        </Box>
                        <Box alignSelf="center">
                            <AddCompanyPipelineButton companyId={company.id} />
                        </Box>
                    </Box>
                    <Box elevation="medium" pad="medium">
                        {pipelines?.length ? (
                            <Box overflow={{ vertical: 'auto' }}>
                                {pipelines.map((pipeline) => (
                                    <Anchor
                                        key={pipeline.id}
                                        href={`/pipelines/${pipeline.id}`}
                                        label={pipeline.attributes.name}
                                    />
                                ))}
                            </Box>
                        ) : (
                            'No pipeline available'
                        )}
                    </Box>
                </Box>
            </PageContent>
            {notification && (
                <Notification
                    toast
                    status={notification.status}
                    title={notification.title}
                    message={notification.message}
                    onClose={() => setNotification(null)}
                />
            )}
        </Page>
    );
};

export const CompanyDashboardSection = ({
    companyId,
    companySlug,
    admins = [],
    reportRecipientsSection = null,
}) => {
    const navigate = useNavigate();
    const [openJobCount, setOpenJobCount] = useState(0);
    const [breakdown, setBreakdown] = useState({ total: 0, stages: [] });
    const [preparingJobCount, setPreparingJobCount] = useState(0);
    const [preparingCandidacyCount, setPreparingCandidacyCount] = useState(0);

    useEffect(() => {
        let isActive = true;

        (async () => {
            const [
                openJobs,
                candidacyBreakdown,
                preparingJobs,
                preparingCandidacies,
            ] = await Promise.all([
                loadOpenJobCount(companySlug),
                loadCandidacyBreakdown(companySlug),
                loadPreparingJobCount(companySlug),
                loadPreparingCandidacyCount(companySlug),
            ]);

            if (isActive) {
                setOpenJobCount(openJobs);
                setBreakdown(candidacyBreakdown);
                setPreparingJobCount(preparingJobs);
                setPreparingCandidacyCount(preparingCandidacies);
            }
        })();

        return () => {
            isActive = false;
        };
    }, [companySlug]);

    return (
        <Box gap="medium">
            <Heading level="3" margin="none">
                Client Dashboard
            </Heading>
            <Box direction="row" gap="medium" wrap>
                <StatCard
                    label="Active Jobs"
                    value={openJobCount}
                    onClick={() =>
                        navigate('/jobs', {
                            state: { companySlug, statusFilter: ['open'] },
                        })
                    }
                />
                <StatCard
                    label="Active Candidates"
                    value={breakdown.total}
                    onClick={() =>
                        navigate(`/companies/${companyId}/candidates`)
                    }
                />
                <StatCard
                    label="Future Jobs"
                    value={preparingJobCount}
                    onClick={() =>
                        navigate('/jobs', {
                            state: { companySlug, statusFilter: ['preparing'] },
                        })
                    }
                />
                <StatCard
                    label="Future Candidates"
                    value={preparingCandidacyCount}
                    onClick={() =>
                        navigate('/jobs', {
                            state: { companySlug, statusFilter: ['preparing'] },
                        })
                    }
                />
            </Box>
            {breakdown.total > 0 && (
                <Box gap="small" pad="medium" elevation="small" round="small">
                    <Text weight="bold">Pipeline Overview</Text>
                    <Meter
                        type="bar"
                        size="full"
                        thickness="medium"
                        max={breakdown.total}
                        values={breakdown.stages.map((stage) => ({
                            value: stage.count,
                            color: stage.color,
                            label: stage.label,
                        }))}
                    />
                    <Box
                        direction="row"
                        wrap
                        gap="small"
                        margin={{ top: 'small' }}>
                        {breakdown.stages.map((stage) => (
                            <Box
                                key={stage.label}
                                direction="row"
                                align="center"
                                gap="xsmall">
                                <Box
                                    background={stage.color}
                                    pad="xsmall"
                                    round="xsmall"
                                    width="12px"
                                    height="12px"
                                />
                                <Text size="small">
                                    {Utils.convertEnumStringToLabel(
                                        stage.label,
                                    )}{' '}
                                    ({stage.count})
                                </Text>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
            <Box fill="horizontal">
                <Heading level="3">Portal Admins</Heading>
                <Box elevation="medium" pad="medium">
                    <DataTable
                        sortable
                        data={admins}
                        columns={[
                            {
                                header: 'Name',
                                property: 'lastName',
                                render: (admin) =>
                                    `${admin.firstName || ''} ${admin.lastName || ''}`.trim() ||
                                    '—',
                                sortable: true,
                            },
                            {
                                header: 'Email',
                                property: 'email',
                                sortable: true,
                            },
                            {
                                header: 'Last Login',
                                property: 'lastLogin',
                                render: (admin) =>
                                    admin.lastLogin
                                        ? new Date(
                                              admin.lastLogin,
                                          ).toLocaleString()
                                        : 'Never',
                                sortable: true,
                            },
                        ]}
                    />
                </Box>
            </Box>
            {reportRecipientsSection}
            <RecentActivity
                companySlug={companySlug}
                contactHref={(contact) => `/contacts/${contact.id}`}
            />
        </Box>
    );
};

const StatCard = ({ label, value, onClick }) => (
    <Box
        pad="medium"
        elevation="small"
        round="small"
        align="center"
        gap="small"
        width="small"
        onClick={onClick}
        role="button"
        a11yTitle={label}
        focusIndicator={false}
        style={{ cursor: 'pointer' }}>
        <Text size="xxlarge" weight="bold">
            {value}
        </Text>
        <Text size="small">{label}</Text>
    </Box>
);

export async function loader({ params }) {
    const companyQuery = `query getCompany($companyId: ID!) {
        company(id: $companyId) {
            data {
                id
                attributes {
                    name
                    slug
                    isContractClient
                    isReportClient
                    hideRaceData
                    reportRecipients
                    jobs {
                        data {
                            id
                            attributes {
                                title
                                status
                                seats
                                matchCount
                                matchSeatRatio
                                interestedCount
                                appliedCount
                            }
                        }
                    }
                    pipelines (filters: {company: {id: {eq: ${params.companyId}}}}, sort: ["name:asc"]) {
                        data {
                            id
                            attributes {
                                name
                            }
                        }
                    }
                    companyAdmins {
                        data {
                            id
                            attributes {
                                firstName
                                lastName
                                email
                                lastLogin
                            }
                        }
                    }
                }
            }
        }
    }`;

    try {
        const response = await API.post('graphql', {
            query: companyQuery,
            variables: { companyId: params.companyId },
        });
        return response.data.data.company.data;
    } catch (error) {
        console.log(error);
        return {};
    }
}

const loadOpenJobCount = async (companySlug) => {
    const query = QueryString.stringify({
        filters: {
            company: { slug: { $eqi: companySlug } },
            status: 'open',
        },
        pagination: { pageSize: 1 },
    });
    try {
        const response = await API.get(`/api/jobs?${query}`);
        return response.data.meta.pagination.total;
    } catch (error) {
        console.log(error);
        return 0;
    }
};

const loadCandidacyBreakdown = async (companySlug) => {
    const results = await Promise.all(
        CANDIDACY_STATUSES.map(async (statusDef) => {
            const query = QueryString.stringify({
                filters: {
                    status: statusDef.label,
                    job: {
                        status: 'open',
                        company: { slug: { $eqi: companySlug } },
                    },
                },
                pagination: { pageSize: 1 },
            });
            try {
                const response = await API.get(`/api/candidacies?${query}`);
                return {
                    ...statusDef,
                    count: response.data.meta.pagination.total,
                };
            } catch (error) {
                console.log(error);
                return { ...statusDef, count: 0 };
            }
        }),
    );

    const stages = results.filter((s) => s.count > 0);
    const total = results.reduce((sum, s) => sum + s.count, 0);
    return { total, stages };
};

const loadPreparingJobCount = async (companySlug) => {
    const query = QueryString.stringify({
        filters: {
            company: { slug: { $eqi: companySlug } },
            status: 'preparing',
        },
        pagination: { pageSize: 1 },
    });
    try {
        const response = await API.get(`/api/jobs?${query}`);
        return response.data.meta.pagination.total;
    } catch (error) {
        console.log(error);
        return 0;
    }
};

const loadPreparingCandidacyCount = async (companySlug) => {
    const query = QueryString.stringify({
        filters: {
            status: { $in: PIPELINE_CANDIDACY_STATUSES },
            job: {
                status: 'preparing',
                company: { slug: { $eqi: companySlug } },
            },
        },
        pagination: { pageSize: 1 },
    });
    try {
        const response = await API.get(`/api/candidacies?${query}`);
        return response.data.meta.pagination.total;
    } catch (error) {
        console.log(error);
        return 0;
    }
};
